import { useState, useEffect } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import CreateAccount from "./steps/CreateAccount";
import PlannerBasicInfo from "./steps/PlannerBasicInfo";
import PlannerExpertise from "./steps/PlannerExpertise";
import ProfessionalRegistrationLayout from "./steps/ProfessionalRegistrationLayout";
import ProfessionalDetails from "./steps/ProfessionalDetails";
import Experience from "./steps/Experience";
import Completion from "./steps/Completion";
import OtpVerification from "./steps/OtpVerification";
import Login from "./steps/Login";
import ForgotPassword from "./steps/ForgotPassword";
import { registerUser, sendOtp, saveRegistrationDraft, finalizeRegistration, getRegistrationDraft } from "../../api/auth";
import { MediaUploadService } from "../../services/MediaUploadService";
import { X } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../store/slices/authSlice";

// Map legacy initialPurpose values (host/planner/artist/volunteer) to new schema field
function buildInitialFormData(initialPurpose) {
  if (!initialPurpose) return {};
  if (initialPurpose === "host" || initialPurpose === "customer")
    return { role: "customer" };
  const roleMap = {
    planner: "planner",
    artist: "performer",
    volunteer: "crew",
    performer: "performer",
    crew: "crew",
  };
  return {
    role: "professional",
    professionalRole: roleMap[initialPurpose] || "",
  };
}

export default function RegistrationModal({
  onClose,
  initialStep = 1,
  initialPurpose,
  onSuccessOverride,
  isForced = false,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [step, setStep] = useState(initialStep); // Step state for registration flow
  const [formData, setFormData] = useState(buildInitialFormData(initialPurpose));
  const [isVisible, setIsVisible] = useState(false); // Control visibility for transitions
  const [isUserCreated, setIsUserCreated] = useState(false); // Tracks if background registration happened
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    setStep(initialStep);
    
    // If opening a resumed registration flow directly, try fetching draft
    if (initialStep > 1 && localStorage.getItem("authToken") && !isUserCreated) {
      const fetchDraft = async () => {
        try {
          const res = await getRegistrationDraft();
          if (res?.draft?.data) {
            const draftData = res.draft.data;
            if (draftData.locationId && draftData.locationName && !draftData.location) {
              draftData.location = { id: draftData.locationId, name: draftData.locationName };
            }
            setFormData(prev => ({ ...prev, ...draftData }));
            setIsUserCreated(true);
          }
        } catch (err) {
          console.error("Could not fetch registration draft:", err);
        }
      };
      fetchDraft();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStep]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const closeModal = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleNext = async (data) => {
    const updatedFormData = { ...formData, ...data };
    setFormData(updatedFormData);

    if (step === 1) {
      const isOAuth = data.isGoogleAuth || data.isFacebookAuth;
      if (isOAuth) {
        if (data.role === "customer") {
          const ok = await submitCustomerRegistration(updatedFormData);
          if (ok) {
            window.dispatchEvent(new CustomEvent("profileUpdated"));
            finishRegistration();
          }
        } else {
          const ok = await submitProfessionalRegistration(updatedFormData);
          if (ok) setStep(2);
        }
      } else {
        try {
          await sendOtp(data.email, data.phone);
          setStep("otp");
        } catch (err) {
          showError(err.message || "Failed to initiate registration.");
          return;
        }
      }
    } else if (step === "otp") {
      if (formData.role === "customer") {
        const ok = await submitCustomerRegistration(updatedFormData);
        if (ok) {
          window.dispatchEvent(new CustomEvent("profileUpdated"));
          finishRegistration();
        }
      } else {
        const ok = await submitProfessionalRegistration(updatedFormData);
        if (ok) setStep(2);
      }
    } else if (step === 2) {
      if (formData.professionalRole && formData.professionalRole !== updatedFormData.professionalRole) {
        updatedFormData.categories = [];
        updatedFormData.categoriesId = [];
        updatedFormData.rawCategories = [];
        updatedFormData.expertise = [];
        updatedFormData.expertiseId = [];
        updatedFormData.rawExpertise = [];
      }
      // Step 2 -> 3: Progressive save Draft
      const ok = await saveDraft(3, updatedFormData);
      if (ok) setStep(3);
    } else if (step === 3) {
      // Step 3 -> 4: Progressive save Draft
      const ok = await saveDraft(4, updatedFormData);
      if (ok) setStep(4);
    } else if (step === 4) {
      // Step 4 -> Finish: Finalize Registration
      const finalizedUser = await finalizeProfile(updatedFormData);
      if (finalizedUser) {
        window.dispatchEvent(new CustomEvent("profileUpdated"));
        dispatch(loginSuccess({ user: finalizedUser, token: localStorage.getItem("authToken") }));
        finishRegistration();
      }
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const handlePrev = (currentData = {}) => {
    if (currentData && typeof currentData === "object" && !currentData.nativeEvent) {
      setFormData(prev => ({ ...prev, ...currentData }));
    }
    if (step === 2) {
      return;
    }
    setStep((prev) => (prev > 1 ? prev - 1 : 1));
  };

  // ─── Registration Helpers ───────────────────────────────────────────────────

  const submitCustomerRegistration = async (data) => {
    try {
      const payload = {
        fullName: data.fullName,
        email: data.email,
        password: data.password,
        role: "customer",
        phone: data.phone,
        otpVerified: data.otpVerified || false,
      };
      if (data.isGoogleAuth) { payload.isGoogleAuth = true; payload.googleToken = data.googleToken; }
      if (data.isFacebookAuth) { payload.isFacebookAuth = true; payload.facebookToken = data.facebookToken; }

      const res = await registerUser(payload);
      saveUserToLocal(res.token, res.user);
      return true;
    } catch (error) {
      showError(error.message || "Registration failed");
      return false;
    }
  };

  const submitProfessionalRegistration = async (data) => {
    try {
      const payload = {
        fullName: data.fullName,
        email: data.email,
        password: data.password,
        role: "professional",
        phone: data.phone,
        otpVerified: data.otpVerified || false,
      };
      if (data.isGoogleAuth) { payload.isGoogleAuth = true; payload.googleToken = data.googleToken; }
      if (data.isFacebookAuth) { payload.isFacebookAuth = true; payload.facebookToken = data.facebookToken; }

      // Register user and create Step 2 Draft
      const res = await registerUser(payload);
      saveUserToLocal(res.token, res.user);
      setIsUserCreated(true);
      return true;
    } catch (error) {
      showError(error.message || "Registration failed");
      return false;
    }
  };


  const saveDraft = async (nextStep, data) => {
    try {
      let profilePictureId = null;
      if (data.profilePicture && typeof data.profilePicture !== "string") {
        const m = await MediaUploadService.uploadMedia(data.profilePicture, { folder: "portfolio", tags: ["registration_temp"] });
        profilePictureId = m._id;
      } else if (typeof data.profilePicture === "string") {
        profilePictureId = data.profilePicture;
      }

      const { password, confirmPassword, location, ...safeData } = data;
      
      const locationId = location && typeof location === 'object' ? location.id : (location || data.locationId);
      const locationName = location && typeof location === 'object' ? location.name : (data.locationName || "");

      const draftPayload = {
        ...safeData, // Save all data so nothing is lost
        companyName: data.accountType === "individual" ? `${data.firstName || ""} ${data.lastName || ""}`.trim() : (data.companyName || ""),
        locationId: locationId || "",
        locationName: locationName || "",
        about: data.bio || "",
        establishmentYear: data.establishmentYear ? Number(data.establishmentYear) : undefined,
        expertise: data.expertise || [],
        expertiseId: data.expertiseId || [],
        subExpertise: data.subExpertise || [],
        subExpertiseId: data.subExpertiseId || [],
        rawSubExpertise: data.rawSubExpertise || [],
        categories: data.categories || [],
        categoriesId: data.categoriesId || [],
        languages: data.languages || [],
        portfolioMedia: data.portfolioMedia || [],
        ...(profilePictureId && { profilePicture: profilePictureId }),
      };

      await saveRegistrationDraft(nextStep, draftPayload);
      if (profilePictureId) {
        setFormData(prev => ({ ...prev, profilePicture: profilePictureId }));
      }
      localStorage.setItem("draftStep", nextStep);
      return true;
    } catch (err) {
      showError("Failed to save progress");
      return false;
    }
  };

  const finalizeProfile = async (data) => {
    try {
      const draftPayload = {
        services: data.services || [],
        additionalDetails: (data.additionalDetails && data.additionalDetails.trim()) ? [data.additionalDetails] : [],
        languages: data.languages || [],
        expertise: data.expertise || [],
        expertiseId: data.expertiseId || [],
        subExpertise: data.subExpertise || [],
        subExpertiseId: data.subExpertiseId || [],
        categories: data.categories || [],
        categoriesId: data.categoriesId || [],
        subCategories: data.subCategories || [],
        subCategoriesId: data.subCategoriesId || [],
      };
      const res = await finalizeRegistration(draftPayload);
      
      // Update local storage with finalized profile info and new token (which contains new profileId)
      const tokenToSave = res.token || localStorage.getItem("authToken");
      saveUserToLocal(tokenToSave, res.user);
      
      return res.user;
    } catch (err) {
      showError(err.message || "Failed to finalize registration");
      return false;
    }
  };

  const saveUserToLocal = (token, user) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("userId", user.id || user._id);
    if (user.customerId) localStorage.setItem("customerId", user.customerId);
    if (user.professionalId) localStorage.setItem("professionalId", user.professionalId);
    localStorage.setItem("role", user.role || "");
    if (user.professionalRole) localStorage.setItem("professionalRole", user.professionalRole);
    localStorage.setItem("fullName", user.fullName);
    localStorage.setItem("email", user.email);
    localStorage.setItem("isVerified", user.isVerified || false);
    
    // Set onboardingComplete to true if profile is fully setup (professionalId/customerId exists)
    const isProfessional = user.role === "professional";
    const onboardingComplete = isProfessional ? !!user.professionalId : true;
    localStorage.setItem("onboardingComplete", String(onboardingComplete));
    
    if (user.phone) localStorage.setItem("phone", user.phone);
    if (user.profilePictureUrl || user.profilePicture) {
      const picUrl = user.profilePictureUrl || user.profilePicture;
      localStorage.setItem("profilePicture", picUrl);
      setFormData((prev) => ({ ...prev, profilePicture: prev.profilePicture || picUrl }));
    }
  };

  const finishRegistration = () => {
    const route = formData.role === "professional" ? "/dashboard/professional" : "/dashboard/customer";
    closeModal();
    if (onSuccessOverride) {
      onSuccessOverride();
    } else {
      navigate(route);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const stepComponents = {
    1: <CreateAccount onNext={handleNext} onGoToLogin={() => setStep("login")} initialData={formData} />,
    otp: <OtpVerification email={formData.email} phone={formData.phone} onVerified={() => handleNext({ otpVerified: true })} onBack={() => setStep(1)} />,
    2: <PlannerBasicInfo onNext={handleNext} onPrev={null} initialData={formData} isForced={isForced || formData.isForced} />,
    3: <PlannerExpertise onNext={handleNext} onPrev={handlePrev} initialData={formData} />,
    4: <ProfessionalDetails onNext={handleNext} onPrev={handlePrev} initialData={formData} />,
    5: <Completion onFinish={finishRegistration} onNext={handleNext} isVerified={formData.isGoogleAuth || formData.isFacebookAuth || formData.otpVerified} />,
    login: <Login onGotoRegister={() => setStep(1)} onGotoForgotPassword={() => setStep("forgot-password")} onClose={closeModal} onSuccessOverride={onSuccessOverride} onResumeRegistration={(step, draftData) => { setFormData(draftData); setStep(step); setIsUserCreated(true); }} />,
    "forgot-password": <ForgotPassword onBackToLogin={() => setStep("login")} />,
  };

  return (
    <Dialog open={true} onClose={closeModal} className="relative z-50">
      <div className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ease-out ${isVisible ? "opacity-100" : "opacity-0"}`} aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-2 sm:p-4">
        <DialogPanel
          className={`
            w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] bg-white rounded-2xl sm:rounded-3xl shadow-2xl relative
            flex flex-col transition-all duration-300 ease-out
            ${isVisible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"}
          `}
        >


          {/* Close button */}
          {step !== 5 && (
            <button onClick={closeModal} className="absolute right-3 top-3 sm:right-4 sm:top-4 z-10 p-2 text-gray-500 hover:text-gray-900 transition-colors">
              <X size={20} />
            </button>
          )}

          {/* Step content */}
          <div className="w-full flex-1 min-h-0 flex flex-col px-3 sm:px-6 lg:px-8 pb-3 pt-3 overflow-y-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {stepComponents[step]}
          </div>

        </DialogPanel>
      </div>
    </Dialog>
  );
}
